import type { ReactNode } from "react";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import { useRouter } from "next/router";
import { useMemo } from "react";

import { USER_PERMISSIONS_ENUM } from "@/constants/permissions";
import { DRAWER_VIEW, MODAL_VIEW } from "@/constants/views";
import { hasPermission } from "@/store/slices/operationalRolesSlice";
import { useAppDispatch, useAppSelector } from "@/store";
import { setLogout } from "@/store/thunks/auth";
import { closeDrawer, openDrawer, openModal } from "@/store/slices/commonSlice";

/**
 * `Y` in `mod-9802.js` — the ONE action list, shared by the navbar chip group
 * (`to` / `NavActions`) and by the mobile order bar's SpeedDial (`q`).
 *
 * It is a hook rather than a constant because every entry's visibility depends
 * on store state, and it is shared rather than duplicated because the two
 * consumers must never disagree about what a customer can reach.
 *
 * Order and copy are verbatim: `Offers` (hidden for staff), `Reservations`
 * (`reservation_enabled`), the account chip labelled with the customer's own
 * `display_name`, and `Login` when signed out.
 */
export interface NavActionMenuItem {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /** Renders in the error colour and sits below a rule. Logout uses it. */
  danger?: boolean;
}

export interface NavAction {
  label: string;
  color: "primary" | "secondary";
  icon: ReactNode;
  onClick?: () => void;
  hide?: boolean;
  menuItems?: NavActionMenuItem[];
  /** Identity block drawn at the top of the dropdown, above the entries. */
  menuHeader?: { title: string; subtitle?: string };
}

export default function useNavActions(): NavAction[] {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const reservationEnabled = useAppSelector(
    (state) => state.settings.data.reservation_enabled,
  );
  const customer = useAppSelector((state) => state.auth.customer);
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const staff = useAppSelector((state) => state.auth.staff);
  // `isLoggedIn` is the original's own flag, not `customer != null` — see
  // the auth slice's header for why the two are not interchangeable.
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn);
  // `d = Al(POS_VIEW_REPORTS)` — the tenant's role matrix decides, not the fact
  // of being staff.
  const canViewReports = useAppSelector((state) =>
    hasPermission(state, USER_PERMISSIONS_ENUM.POS_VIEW_REPORTS),
  );

  return useMemo<NavAction[]>(
    () => [
      {
        label: "Offers",
        color: "primary",
        icon: <LocalOfferIcon fontSize="small" />,
        onClick: () => dispatch(openDrawer(DRAWER_VIEW.OFFERS)),
        hide: isStaff,
      },
      {
        label: "Reservations",
        color: "primary",
        icon: <EventAvailableOutlinedIcon fontSize="small" />,
        onClick: () => dispatch(openModal(MODAL_VIEW.RESERVATION)),
        hide: !reservationEnabled,
      },
      {
        label: customer?.name || "Account",
        color: "secondary",
        icon: <PersonIcon fontSize="medium" />,
        hide: !isLoggedIn,
        // Who is actually signed in. An operator and a customer reach the same
        // chip, and on a shared floor terminal "which account am I on?" is a
        // real question — so the dropdown answers it before offering actions.
        menuHeader: {
          title: staff?.display_name || customer?.name || "Account",
          subtitle: isStaff
            ? [ "Staff", staff?.role ].filter(Boolean).join(" · ")
            : customer?.phone || undefined,
        },
        // The original's account menu is X Report + Staff Orders (staff only,
        // behind POS_VIEW_REPORTS), Update Details and Logout. There is no
        // "Recent Orders" entry: order history lives inline on the menu page as
        // the recent-orders rail, and the drawer it opens holds ONE order.
        menuItems: [
          ...(isStaff ? [{ label: "Staff details / switch staff", icon: <ManageAccountsOutlinedIcon fontSize="small" />, onClick: () => dispatch(openModal(MODAL_VIEW.STAFF_INFO_JOIN)) }] : []),
          // `...(i && d ? [ … ] : [])` — BOTH conditions, and they are not the
          // same question. `isStaff` is who you are; `POS_VIEW_REPORTS` is what
          // your role may see. An operator whose `allowed_platforms` omits WEB
          // passes the first and fails the second, so they get a Staff chip and
          // no reports — which is the point of the three-state platform test.
          ...(isStaff && canViewReports
            ? [
                {
                  label: "X Report",
                  icon: <SummarizeOutlinedIcon fontSize="small" />,
                  onClick: () => dispatch(openDrawer(DRAWER_VIEW.X_REPORT)),
                },
                {
                  label: "Staff Orders",
                  icon: <ReceiptLongOutlinedIcon fontSize="small" />,
                  onClick: () => dispatch(openDrawer(DRAWER_VIEW.STAFF_ORDERS)),
                },
              ]
            : []),
          // UPDATE DETAILS IS A CUSTOMER ACTION. It opens the consumer-details
          // drawer, which edits the *diner's* name/phone/address on the order —
          // nothing an operator taking orders on the floor should be editing,
          // and on a staff session there is no consumer to edit yet. Hidden for
          // staff on the operator's own instruction.
          ...(isStaff
            ? []
            : [
                {
                  label: "Update Details",
                  icon: <ManageAccountsOutlinedIcon fontSize="small" />,
                  onClick: () => dispatch(openDrawer(DRAWER_VIEW.CONSUMER_DETAILS)),
                },
              ]),
          {
            label: "Logout",
            danger: true,
            icon: <LogoutIcon fontSize="small" />,
            onClick: () => {
              dispatch(closeDrawer());
              void dispatch(setLogout());
            },
          },
        ],
      },
      {
        // THE STAFF FLOOR PLAN. The one way into `/staff/tables`, and it is
        // exactly where the POS's own Dine In lives on its navigation — an
        // operator taking orders on the floor should not have to type a URL.
        label: "Floor Plan",
        color: "primary",
        icon: <TableRestaurantIcon fontSize="medium" />,
        onClick: () => {
          dispatch(closeDrawer());
          void router.push("/staff/tables");
        },
        hide: !isStaff,
      },
      {
        label: "Login",
        color: "primary",
        icon: <PersonIcon fontSize="medium" />,
        onClick: () => dispatch(openDrawer(DRAWER_VIEW.VERIFICATION)),
        // `isStaff` as well as `isLoggedIn`. A staff session is signed in, so
        // the second flag normally hides this anyway — but an operator who
        // reached the staff role through the access-code switch rather than
        // through an OTP has `isLoggedIn: false` and WOULD have been offered a
        // customer login that ends their own shift. See `VerificationDrawer`.
        hide: isLoggedIn || isStaff,
      },
    ],
    [
      canViewReports,
      dispatch,
      customer?.name,
      customer?.phone,
      staff?.display_name,
      staff?.role,
      isLoggedIn,
      isStaff,
      reservationEnabled,
      router,
    ],
  );
}
